import React, { Suspense } from 'react';
import { fbPageView } from './utils/analytics';

import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

import { HelmetProvider } from 'react-helmet-async';
import { ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import { AuthProvider } from './AuthContext';
import { CategoryProvider } from './CategoryContext';
import AppLayout from './components/Layout';
import InstallPWAPrompt from './components/InstallPWAPrompt';
import NotFound from './pages/NotFound';












import SupportFab from './components/SupportFab';
import CookieConsent from './components/CookieConsent';

import ErrorBoundary from './components/ErrorBoundary';
import OnboardingTour from './components/OnboardingTour';
import Home from './pages/Home';



























import './App.css';

// Routes are code-split: each page is fetched only when it is first visited.
const About = React.lazy(() => import('./pages/About'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Categories = React.lazy(() => import('./pages/Categories'));
const CategoryDetail = React.lazy(() => import('./pages/CategoryDetail'));
const Chat = React.lazy(() => import('./pages/Chat'));
const CreateGig = React.lazy(() => import('./pages/CreateGig'));
const CreateProject = React.lazy(() => import('./pages/CreateProject'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Favorites = React.lazy(() => import('./pages/Favorites'));
const FreelancerDashboard = React.lazy(() => import('./pages/FreelancerDashboard'));
const FreelancerProfile = React.lazy(() => import('./pages/FreelancerProfile'));
const Freelancers = React.lazy(() => import('./pages/Freelancers'));
const GigDetail = React.lazy(() => import('./pages/GigDetail'));
const GigList = React.lazy(() => import('./pages/GigList'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const HireCategory = React.lazy(() => import('./pages/HireCategory'));
const KycSubmit = React.lazy(() => import('./pages/KycSubmit'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Membership = React.lazy(() => import('./pages/Membership'));
const Moderation = React.lazy(() => import('./pages/Moderation'));
const MyGigs = React.lazy(() => import('./pages/MyGigs'));
const MyProjects = React.lazy(() => import('./pages/MyProjects'));
const OrderDetailExperience = React.lazy(() => import('./pages/OrderDetailExperience'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Organizations = React.lazy(() => import('./pages/Organizations'));
const PaymentMethods = React.lazy(() => import('./pages/PaymentMethods'));
const Press = React.lazy(() => import('./pages/Press'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const Projects = React.lazy(() => import('./pages/Projects'));
const ProposalCreate = React.lazy(() => import('./pages/ProposalCreate'));
const Refer = React.lazy(() => import('./pages/Refer'));
const Register = React.lazy(() => import('./pages/Register'));
const SavedSearches = React.lazy(() => import('./pages/SavedSearches'));
const Search = React.lazy(() => import('./pages/Search'));
const Security2FA = React.lazy(() => import('./pages/Security2FA'));
const Support = React.lazy(() => import('./pages/Support'));
const SupportTicket = React.lazy(() => import('./pages/SupportTicket'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const Wallet = React.lazy(() => import('./pages/Wallet'));



/** Fires a Meta Pixel PageView on every SPA route change (the initial one is fired in the head). */
/** Shown for the moment a route's chunk is being fetched. */
function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="lc-route-spinner" aria-label="loading" />
    </div>
  );
}

function PixelRouteTracker() {
  const location = useLocation();
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    fbPageView();
  }, [location.pathname]);
  return null;
}

const App = () => {
  const [lang, setLang] = React.useState(localStorage.getItem('lang') || 'ar');
  const isRTL = lang === 'ar';

  const toggleLang = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };

  React.useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang, isRTL]);

  return (
    <HelmetProvider><ConfigProvider direction={isRTL ? 'rtl' : 'ltr'} locale={isRTL ? arEG : undefined}
      theme={{
        token: {
          colorPrimary: '#0D7377',
          colorInfo: '#0D7377',
          colorSuccess: '#0D7377',
          colorWarning: '#F4A623',
          colorTextBase: '#14213D',
          colorBgBase: '#FFFFFF',
          colorBgLayout: '#EEF3F8',
          borderRadius: 12,
          fontFamily: "'Cairo', -apple-system, BlinkMacSystemFont, sans-serif",
        },
        components: {
          Button: { controlHeight: 40, borderRadius: 12 },
          Input:  { controlHeight: 40, borderRadius: 12 },
          Card:   { borderRadius: 18 },
          Modal:  { borderRadius: 22 },
        },
      }}>
      <AuthProvider>
        <CategoryProvider>
        <Router>
          <PixelRouteTracker />
          <AppLayout lang={lang} toggleLang={toggleLang}>
        <OnboardingTour lang={lang} />
            <ErrorBoundary lang={lang}><Suspense fallback={<RouteFallback />}><Routes>
              <Route path="/" element={<Home lang={lang} />} />
              <Route path="/login" element={<Login lang={lang} />} />
              <Route path="/register" element={<Register lang={lang} />} />
              <Route path="/gigs" element={<GigList lang={lang} />} />
              <Route path="/categories" element={<Categories lang={lang} />} />
              <Route path="/categories/:parentSlug" element={<CategoryDetail lang={lang} />} />
              <Route path="/gigs/create" element={<CreateGig lang={lang} />} />
              <Route path="/gigs/:slug/edit" element={<CreateGig lang={lang} />} />
              <Route path="/gigs/:slug" element={<GigDetail lang={lang} />} />
              <Route path="/my-gigs" element={<MyGigs lang={lang} />} />
              <Route path="/profile" element={<Profile lang={lang} />} />
              <Route path="/orders" element={<Orders lang={lang} />} />
              <Route path="/freelancers" element={<Freelancers lang={lang} />} />
              <Route path="/freelancers/:username" element={<FreelancerProfile lang={lang} />} />
              <Route path="/projects" element={<Projects lang={lang} />} />
              <Route path="/projects/create" element={<CreateProject lang={lang} />} />
              <Route path="/my-projects" element={<MyProjects lang={lang} />} />
              <Route path="/legal/:type" element={<LegalPage lang={lang} />} />
              <Route path="/projects/:id/proposals/create" element={<ProposalCreate lang={lang} />} />
                          <Route path="/orders/:id" element={<OrderDetailExperience lang={lang} />} />
                          <Route path="/admin-panel" element={<AdminDashboard lang={lang} />} />
              <Route path="/help" element={<HelpCenter lang={lang} />} />
              <Route path="/support" element={<Support lang={lang} />} />
              <Route path="/support/:number" element={<SupportTicket lang={lang} />} />
              <Route path="/faq" element={<HelpCenter lang={lang} />} />
              <Route path="/verify-email" element={<VerifyEmail lang={lang} />} />
              <Route path="/chat" element={<Chat lang={lang} />} />
              <Route path="/moderation" element={<Moderation lang={lang} />} />
                          <Route path="/wallet" element={<Wallet lang={lang} />} />
                          <Route path="/projects/:id" element={<ProjectDetail lang={lang} />} />
                          <Route path="/account/kyc" element={<KycSubmit lang={lang} />} />
                          <Route path="/membership" element={<Membership lang={lang} />} />
                          <Route path="/account/orgs" element={<Organizations lang={lang} />} />
                          <Route path="/search" element={<Search lang={lang} />} />
                          <Route path="/projects/:id/proposals/create" element={<ProposalCreate lang={lang} />} />
                          <Route path="/orders/:id" element={<OrderDetailExperience lang={lang} />} />
                          <Route path="/dashboard" element={<FreelancerDashboard lang={lang} />} />
                          <Route path="/about" element={<About lang={lang} />} />
                          <Route path="/hire/:slug" element={<HireCategory lang={lang} />} />
                          <Route path="/press" element={<Press lang={lang} />} />
                          <Route path="/blog" element={<Blog lang={lang} />} />
                          <Route path="/blog/:slug" element={<BlogPost lang={lang} />} />
                          <Route path="/refer" element={<Refer lang={lang} />} />
                          <Route path="/saved-searches" element={<SavedSearches lang={lang} />} />
                          <Route path="/favorites" element={<Favorites lang={lang} />} />
                          <Route path="/account/settings" element={<AccountSettings lang={lang} />} />
                          <Route path="/account/payments" element={<PaymentMethods lang={lang} />} />
                          <Route path="/account/2fa" element={<Security2FA lang={lang} />} />
                          <Route path="*" element={<NotFound lang={lang} />} />
            </Routes></Suspense></ErrorBoundary>
          </AppLayout>
          <SupportFab />
          <InstallPWAPrompt lang={lang} />
          <CookieConsent />
      </Router>
      </CategoryProvider></AuthProvider>
    </ConfigProvider></HelmetProvider>
  );
};

export default App;
